/**
 * LandingPage Component - Neutral Multi-Industry Version
 * Shows images from various industries: Construction, Lab, Aerospace, Shipbuilding, International
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, HelpCircle, Building2, FlaskConical, Plane, Ship, Globe, PlayCircle, TreePine } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import HelpPage from './help/HelpPage';
import OnboardingTutorial from './help/OnboardingTutorial';
import LegalPage from './LegalPage';
import { Button } from './ui/button';

// Multi-industry slides - using verified WIDE landscape images
// Construction and Shipbuilding use AI-generated panoramic images for consistent 16:9 format
const SLIDES = [
  {
    // Construction site panorama - AI generated wide landscape
    url: 'https://static.prod-images.emergentagent.com/jobs/2d2cab4b-4912-45c0-bd5b-26eeb5e1dfff/images/479be66a17b40544cf23ee9239c41ce1151b6a5935c94b03ca6520c3bab6413b.png',
    captionKey: 'landing.neutral.slide1',
    industry: 'bau'
  },
  {
    // Modern laboratory equipment - verified landscape
    url: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=1200&h=675&fit=crop',
    captionKey: 'landing.neutral.slide2',
    industry: 'labor'
  },
  {
    // Airplane manufacturing / aerospace - wide angle (works)
    url: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=1200&h=675&fit=crop&crop=center',
    captionKey: 'landing.neutral.slide3',
    industry: 'aerospace'
  },
  {
    // Container port panorama - AI generated wide landscape
    url: 'https://static.prod-images.emergentagent.com/jobs/2d2cab4b-4912-45c0-bd5b-26eeb5e1dfff/images/ffab5e6a7855ea378204edf8966c473b1b1d9ad9358aae874cfbdb4f6fbf1c0b.png',
    captionKey: 'landing.neutral.slide4',
    industry: 'shipbuilding'
  },
  {
    // Team meeting with laptop - (works)
    url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=675&fit=crop&crop=center',
    captionKey: 'landing.neutral.slide5',
    industry: 'international'
  }
];

// Industry links for quick navigation
const INDUSTRIES = [
  { id: 'bau', icon: Building2, labelKey: 'industry.bau.short', labelFallback: 'Bau', color: 'amber' },
  { id: 'labor', icon: FlaskConical, labelKey: 'industry.labor.short', labelFallback: 'Labor', color: 'blue' },
  { id: 'aerospace', icon: Plane, labelKey: 'industry.aerospace.short', labelFallback: 'Luftfahrt', color: 'sky' },
  { id: 'shipbuilding', icon: Ship, labelKey: 'industry.shipbuilding.short', labelFallback: 'Schiffbau', color: 'cyan' },
  { id: 'international', icon: Globe, labelKey: 'industry.international.short', labelFallback: 'International', color: 'emerald' },
  { id: 'galabau', icon: TreePine, labelKey: 'industry.galabau.short', labelFallback: 'GaLaBau', color: 'green' }
];

const LandingPage = ({ onClickToRegister, onClickToLogin }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showLegal, setShowLegal] = useState(false);

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
        setIsTransitioning(false);
      }, 300);
    }, 3500); // 3.5 seconds per slide

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = useCallback((index) => {
    setIsAutoPlaying(false);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
      // Resume auto-play after 10 seconds of inactivity
      setTimeout(() => setIsAutoPlaying(true), 10000);
    }, 300);
  }, []);

  const goNext = useCallback(() => {
    goToSlide((currentSlide + 1) % SLIDES.length);
  }, [currentSlide, goToSlide]);

  const goPrev = useCallback(() => {
    goToSlide((currentSlide - 1 + SLIDES.length) % SLIDES.length);
  }, [currentSlide, goToSlide]);

  return (
    <div className="h-full bg-gradient-to-b from-sky-100 to-emerald-100 flex flex-col" style={{ height: '100%', minHeight: '100%' }}>
      {/* Header with Language Selector */}
      <header className="flex justify-between items-center px-4 py-3 sm:px-6 sm:py-4 flex-shrink-0 relative z-10">
        {/* Language Selector */}
        <LanguageSelector />
        
        {/* Help Button - smaller, for documentation */}
        <button
          onClick={() => setShowHelp(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 hover:bg-white text-gray-600 hover:text-emerald-700 rounded-full shadow-sm transition-all hover:shadow-md text-sm"
          data-testid="landing-help-button"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="hidden sm:inline">{t('help.title', 'Help')}</span>
        </button>
      </header>
      
      {/* Help Page Modal */}
      {showHelp && (
        <HelpPage 
          onClose={() => setShowHelp(false)} 
          currentUser={null}
        />
      )}
      
      {/* Onboarding Tutorial Modal */}
      {showTutorial && (
        <OnboardingTutorial 
          onComplete={() => setShowTutorial(false)} 
          currentUser={null}
        />
      )}
      
      {/* Legal Page Modal */}
      {showLegal && (
        <LegalPage isOpen={showLegal} onClose={() => setShowLegal(false)} />
      )}
      
      {/* Main Slideshow Area */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-4 min-h-0">
        {/* Slideshow Container */}
        <div className="relative w-full max-w-4xl mx-auto">
          {/* Navigation Arrows - Desktop */}
          <button
            onClick={goPrev}
            className="hidden md:flex absolute left-[-60px] top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white rounded-full items-center justify-center shadow-lg transition-all z-10"
            aria-label={t('landing.prevImage', 'Vorheriges Bild')}
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          
          <button
            onClick={goNext}
            className="hidden md:flex absolute right-[-60px] top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white rounded-full items-center justify-center shadow-lg transition-all z-10"
            aria-label={t('landing.nextImage', 'Nächstes Bild')}
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          {/* Image Container with Click-through */}
          <div 
            className={`relative cursor-pointer transform transition-all duration-300 ${
              isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            }`}
            onClick={goNext}
          >
            {/* Slide Image - fixed 16:9 aspect ratio, image fills entire container */}
            <div className="relative w-full overflow-hidden rounded-2xl shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              <img 
                src={SLIDES[currentSlide].url}
                alt={t(SLIDES[currentSlide].captionKey)}
                className="absolute top-0 left-0 w-full h-full object-cover object-center"
                loading="eager"
              />
            </div>
          </div>
          
          {/* Caption below image - large centered text */}
          <div className={`mt-6 text-center transition-all duration-300 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 px-4 leading-tight">
              {t(SLIDES[currentSlide].captionKey)}
            </h2>
            
            {/* "So einfach funktioniert es!" Button - Opens Tutorial */}
            <button
              onClick={() => setShowTutorial(true)}
              className="mt-4 flex items-center justify-center gap-2 mx-auto px-5 py-2.5 bg-white hover:bg-emerald-50 text-emerald-700 font-medium rounded-full shadow-md hover:shadow-lg transition-all border border-emerald-200"
              data-testid="landing-tutorial-button"
            >
              <PlayCircle className="w-5 h-5" />
              {t('landing.howItWorks', 'So einfach funktioniert es!')}
            </button>
            
            {/* Register Button */}
            <Button
              onClick={onClickToRegister}
              className="mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="landing-register-button"
            >
              {t('landing.registerFree', 'Register for free')} →
            </Button>
          </div>

          {/* Mobile Navigation Arrows */}
          <div className="flex md:hidden justify-between mt-4">
            <button
              onClick={goPrev}
              className="w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
              aria-label={t('landing.prevImage', 'Previous image')}
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            
            <button
              onClick={goNext}
              className="w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
              aria-label={t('landing.nextImage', 'Next image')}
            >
              <ChevronRight className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-emerald-600 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Gehe zu Slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-3xl mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${((currentSlide + 1) / SLIDES.length) * 100}%` }}
          />
        </div>

        {/* Mobile CTA Button */}
        <button
          onClick={onClickToRegister}
          className="mt-6 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 sm:hidden"
        >
          {t('landing.tryFree')} ▶
        </button>
        
        {/* Industry Quick Links */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {INDUSTRIES.map((ind) => (
            <a
              key={ind.id}
              href={`/${ind.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all
                ${ind.color === 'amber' ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : ''}
                ${ind.color === 'blue' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : ''}
                ${ind.color === 'sky' ? 'bg-sky-100 text-sky-700 hover:bg-sky-200' : ''}
                ${ind.color === 'cyan' ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200' : ''}
                ${ind.color === 'emerald' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : ''}
                ${ind.color === 'green' ? 'bg-green-100 text-green-700 hover:bg-green-200' : ''}
              `}
              data-testid={`industry-link-${ind.id}`}
            >
              <ind.icon className="w-4 h-4" />
              {t(ind.labelKey, ind.labelFallback)}
            </a>
          ))}
        </div>
        
        {/* Footer hint */}
        <p className="mt-4 text-gray-500 text-sm text-center">
          {t('auth.alreadyHaveAccount')} 
          <button 
            onClick={onClickToLogin}
            className="text-emerald-600 hover:text-emerald-700 font-medium ml-1 underline"
          >
            {t('auth.loginHere')}
          </button>
        </p>
        
        {/* Legal Footer */}
        <div className="mt-8 pt-4 border-t border-gray-200 flex flex-wrap justify-center gap-4 text-xs text-gray-500">
          <button 
            onClick={() => setShowLegal(true)}
            className="hover:text-emerald-600 transition-colors"
          >
            Impressum
          </button>
          <span className="text-gray-300">|</span>
          <button 
            onClick={() => setShowLegal(true)}
            className="hover:text-emerald-600 transition-colors"
          >
            Datenschutz
          </button>
          <span className="text-gray-300">|</span>
          <button 
            onClick={() => setShowLegal(true)}
            className="hover:text-emerald-600 transition-colors"
          >
            AGB
          </button>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
